// TrialMonitoringDashboard.jsx — PORTAL Trial Monitoring Dashboard
// Section 1: CONSORT Participant Flow Table
// Section 2: Data Quality Indicators

import React, { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import api from "./api/axios";
import { useAuth } from "./context/AuthContext";
import DashboardWorkspaceLayout from "./components/dashboard/DashboardWorkspaceLayout";
import GlobalSiteFilter, { siteQueryParams } from "./components/dashboard/GlobalSiteFilter";
import { isGlobalUser, canSeeAnalytics } from "./utils/roles";
import { siteShortCode } from "./utils/siteNames";
import "./TrialMonitoringDashboard.css";
import DataQuality from "./DataQuality";
import ClinicalQuality from "./ClinicalQuality";
import BaselineChars from "./BaselineChars";
import SafetyEvents from "./SafetyEvents";
import EnrollmentForecast from "./EnrollmentForecast";

const ROW_TYPE_CLASS = {
  awaiting: "tmd-row-awaiting",
  ltfu: "tmd-row-ltfu",
  died: "tmd-row-died",
};

/* "i" info icon showing a row's `source` (where its number comes from) on
   hover — a <button> so it's also reachable by keyboard focus and by tap
   on touch devices (mobile has no :hover), not just mouse hover.

   The tooltip itself is rendered through a portal to document.body with
   viewport-fixed coordinates, NOT as a normal absolutely-positioned
   descendant of the table cell. A <td> in a border-collapse:collapse
   table has its own cell-background paint order that can cover an
   absolutely-positioned descendant with the NEXT row's background
   regardless of z-index — a well-known table-layout quirk, reported live
   ("hover info hidden by the next row"). Escaping to a body-level portal
   sidesteps that entirely: the tooltip paints in its own layer, on top of
   everything, independent of the table's internal stacking. */
function InfoTip({ text }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);

  const show = useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({ left: r.left + r.width / 2, top: r.bottom + 6 });
    setOpen(true);
  }, []);
  const hide = useCallback(() => setOpen(false), []);

  if (!text) return null;
  return (
    <span className="tmd-info-wrap">
      <button
        type="button"
        ref={btnRef}
        className="tmd-info-icon"
        aria-label="Where this number comes from"
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
      >
        i
      </button>
      {open && pos && createPortal(
        <span
          className="tmd-info-tip"
          role="tooltip"
          style={{ left: pos.left, top: pos.top }}
        >
          {text}
        </span>,
        document.body
      )}
    </span>
  );
}

function Row({ row, sites, depth = 0 }) {
  const rowClass = ROW_TYPE_CLASS[row.row_type] || "";
  return (
    <>
      <tr className={`tmd-row tmd-depth-${depth} ${rowClass}`}>
        <td className="tmd-label-cell" style={{ paddingLeft: 14 + depth * 18 }}>
          {depth > 0 && <span className="tmd-dash">—</span>}
          <span className="tmd-label-text">{row.label}</span>
          <InfoTip text={row.source} />
        </td>
        <td className="tmd-num tmd-overall">{row.overall}</td>
        {sites.map((site) => (
          <td key={site} className="tmd-num">{row.by_site?.[site] ?? 0}</td>
        ))}
      </tr>
      {(row.sub_rows || []).map((sub, i) => (
        <Row key={`${row.label}-sub-${i}`} row={sub} sites={sites} depth={depth + 1} />
      ))}
      {(row.ltfu_reasons || []).map((sub, i) => (
        <Row key={`${row.label}-reason-${i}`} row={{ ...sub, label: `Reason: ${sub.label}` }} sites={sites} depth={depth + 1} />
      ))}
    </>
  );
}

const SECTIONS = [
  { key: "consort",          label: "Section 1 — CONSORT Flow" },
  { key: "data-quality",     label: "Section 2 — Data Quality" },
  { key: "clinical-quality", label: "Section 3 — Clinical Quality" },
  { key: "baseline",         label: "Section 4 — Baseline Chars" },
  { key: "safety",           label: "Section 5 — Safety & AEs" },
  { key: "enrollment-forecast", label: "Section 6 — Enrolment Forecast" },
];

export default function TrialMonitoringDashboard() {
  const { user } = useAuth();
  const isGlobal = isGlobalUser(user);
  const [activeSection, setActiveSection] = useState("consort");
  const [apiSite, setApiSite] = useState("");

  const [consortData, setConsortData] = useState(null);
  const [consortLoading, setConsortLoading] = useState(true);
  const [consortError, setConsortError] = useState(null);

  const loadConsort = useCallback(async () => {
    setConsortLoading(true);
    setConsortError(null);
    try {
      const res = await api.get("/dashboard/consort", { params: siteQueryParams(apiSite) });
      setConsortData(res.data);
    } catch (err) {
      setConsortError(err.response?.data?.detail || "Failed to load CONSORT flow data");
    } finally {
      setConsortLoading(false);
    }
  }, [apiSite]);

  useEffect(() => { loadConsort(); }, [loadConsort]);

  const downloadCsv = async () => {
    try {
      const res = await api.get("/dashboard/consort?format=csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "consort_flow.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      alert("CSV export failed.");
    }
  };

  return (
    <DashboardWorkspaceLayout pageTitle="Trial Monitoring" onRefresh={loadConsort}>
    <div className="tmd-root">
      <div className="tmd-topbar">
        <div>
          <div className="tmd-title">Trial Monitoring Dashboard</div>
          <div className="tmd-section-tabs">
            {SECTIONS.map(s => (
              <button
                key={s.key}
                className={`tmd-tab ${activeSection === s.key ? "tmd-tab-active" : ""}`}
                onClick={() => setActiveSection(s.key)}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
        <div className="tmd-topbar-actions">
          {isGlobal && (
            <GlobalSiteFilter
              value={apiSite}
              onChange={setApiSite}
              sites={consortData?.sites}
            />
          )}
          {canSeeAnalytics(user) && consortData && activeSection === "consort" && (
            <button className="tmd-csv-btn" onClick={downloadCsv}>Download CSV</button>
          )}
        </div>
      </div>

      {activeSection === "consort" && (
        <div className="tmd-card">
          {consortLoading && <div className="tmd-state">Loading CONSORT flow…</div>}
          {consortError && <div className="tmd-state tmd-error">{consortError}</div>}

          {!consortLoading && !consortError && consortData && (
            <>
              <table className="tmd-table">
                <thead>
                  <tr>
                    <th className="tmd-label-cell">Label</th>
                    <th className="tmd-num">Overall</th>
                    {consortData.sites.map((site) => (
                      <th key={site} className="tmd-num">{siteShortCode(site)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {consortData.rows.map((row) => (
                    <Row key={row.box} row={row} sites={consortData.sites} />
                  ))}
                </tbody>
              </table>

              {(consortData.footnotes || []).map((note, i) => (
                <div key={i} className="tmd-footnote">* {note}</div>
              ))}

              <div className="tmd-timestamp">
                Data as of {new Date(consortData.generated_at).toLocaleString()}
              </div>
            </>
          )}
        </div>
      )}

      {activeSection === "data-quality" && (
        <div className="tmd-card">
          <DataQuality apiSite={apiSite} />
        </div>
      )}

      {activeSection === "clinical-quality" && (
        <div className="tmd-card">
          <ClinicalQuality />
        </div>
      )}

      {activeSection === "baseline" && (
        <div className="tmd-card">
          <BaselineChars />
        </div>
      )}

      {activeSection === "safety" && (
        <div className="tmd-card">
          <SafetyEvents />
        </div>
      )}

      {activeSection === "enrollment-forecast" && (
        <div className="tmd-card">
          <EnrollmentForecast />
        </div>
      )}
    </div>
    </DashboardWorkspaceLayout>
  );
}
